import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import Select from '../Select'
import { AreaChoice, QuestionBlock, TeamBanner } from './SurveyInputs'
import { formAreas, partnerTeam } from '../../lib/surveyPartners'
import type { SurveyAnswer, SurveyDesign } from '../../types'

/** The survey form as someone in a chosen department will see it, with their
 *  partner team filled in. Answers can be tried out; nothing is sent. */
export function FormPreview({ title, design, onClose }: { title: string; design: SurveyDesign; onClose: () => void }) {
  const [as, setAs] = useState(design.departments[0]?.id ?? '')
  const [answers, setAnswers] = useState<Record<string, SurveyAnswer>>({})
  const [area, setArea] = useState('')
  const areas = formAreas(design, as)
  const chosen = areas.find(a => a.id === area)
  const partner = chosen ?? partnerTeam(design, as)

  useEffect(() => {
    const close = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', close)
    return () => window.removeEventListener('keydown', close)
  }, [onClose])

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 overflow-y-auto" onClick={onClose}>
      <div className="max-w-2xl mx-auto my-10 px-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold text-white">Preview</span>
          <div className="flex items-center gap-2">
            {design.departments.length > 0 && (
              <Select value={as} onChange={id => { setAs(id); setArea('') }} options={design.departments.map(d => [d.id, `As ${d.name}`] as const)} className="w-56" />
            )}
            <button type="button" onClick={onClose} className="p-2 rounded-lg bg-white/90 text-slate-600 hover:bg-white" aria-label="Close preview">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-[#005EB8] px-8 py-6">
            <img src="/InvertedLogo.png" alt="Danone" className="h-10 mb-4" />
            <h1 className="text-2xl font-bold text-white">{title}</h1>
            {(areas.length > 0 || !partner.team) && (
              <p className="text-white/80 text-sm mt-1">{areas.length ? chosen?.name ?? design.departments.find(d => d.id === as)?.name : partner.partner}</p>
            )}
            {!areas.length && partner.team && <TeamBanner team={partner.team} supports={partner.supports ?? []} mine={design.departments.find(d => d.id === as)?.name} />}
          </div>
          <div className="p-8">
            <p className="text-slate-500 text-sm mb-6">Your answers are anonymous; only your department is recorded.</p>
            {areas.length > 0 && <AreaChoice areas={areas} value={area} onChange={setArea} />}
            {(!areas.length || chosen) && design.questions.map((q, i) => (
              <QuestionBlock key={q.id || i} question={q} index={i} partner={partner}
                value={answers[q.id || String(i)]} onChange={v => setAnswers(a => ({ ...a, [q.id || String(i)]: v }))} />
            ))}
            <button type="button" disabled className="w-full btn-primary text-white py-3.5 rounded-xl font-bold text-sm opacity-50">Send Answers</button>
          </div>
        </div>
      </div>
    </div>
  )
}
